import { useEffect, useMemo, useState } from "react";
import { FONT, COLOR, CHIP_TONES } from "../lib/tokens";
import { supabase } from "../lib/supabase";
import { useNutritionTally } from "../lib/useNutritionTally";
import ModalSheet from "./ModalSheet";

// NutritionDashboard — per-user macros rollup embedded on the
// self-viewed UserProfile. Renders today's totals by default, with
// WEEK / MONTH tabs that swap to aggregate numbers + a sparkline.
// Each macro stat row carries a progress bar against the user's
// configured target (profiles.nutrition_targets). Tap any row to
// edit targets via the GoalEditor overlay.
//
// Source of truth: cook_logs.nutrition (per-serving macros stamped
// at cook-complete via recipeNutrition) × cook_logs.servings_per_eater
// (how many servings each eater consumed). useNutritionTally does the
// chef-OR-diner query, the day bucketing, and the 7/30-day series.
//
// Props:
//   userId              — self only; dashboard is gated on isSelf in parent
//   targets             — profile.nutrition_targets (kcal/protein_g/fat_g/carb_g)
//   onUpdateTargets(patch) — writes through useUserProfile.setNutritionTargets
//
// CLAUDE.md reserves tan/orange/blue/purple/yellow for identity axes.
// Picked below palette to avoid collision:
//   kcal    — #d4a87a muted gold (distinct from INGREDIENTS yellow)
//   protein — #7ec87e green      (matches pantry-override badge)
//   fat     — #d4743e burnt      (distinct from CATEGORY orange)
//   carbs   — #8ac8e8 sky        (distinct from STORED IN blue)

const MACRO_DEFS = [
  { key: "kcal",      label: "CALORIES", unit: "kcal", color: CHIP_TONES.canonical.fg },
  { key: "protein_g", label: "PROTEIN",  unit: "g",    color: COLOR.leafMuted },
  { key: "fat_g",     label: "FAT",      unit: "g",    color: "#d4743e" },
  { key: "carb_g",    label: "CARBS",    unit: "g",    color: "#8ac8e8" },
];

const DEFAULT_TARGETS = { kcal: 2000, protein_g: 150, fat_g: 65, carb_g: 250 };

const PERIODS = [
  { id: "day",   label: "TODAY",       multiplier: 1  },
  { id: "week",  label: "THIS WEEK",   multiplier: 7  },
  { id: "month", label: "THIS MONTH",  multiplier: 30 },
];

export default function NutritionDashboard({ userId, targets, onUpdateTargets }) {
  const [period, setPeriod]      = useState("day");
  const [editingGoals, setEditingGoals] = useState(false);

  const tally = useNutritionTally(userId);
  const eff = { ...DEFAULT_TARGETS, ...(targets || {}) };

  // XP: +25 when today's totals clear ≥90% of target on all four
  // macros (kcal + protein + fat + carb). Fires once per render cycle
  // where the condition is met; the 1/day cap on xp_source_values
  // keeps the actual credit to one per local day regardless of how
  // many times the dashboard re-renders or the user reopens it.
  useEffect(() => {
    if (!userId) return;
    const t = tally.today || {};
    const macros = ["kcal", "protein_g", "fat_g", "carb_g"];
    const hit = macros.every(k => {
      const tgt = Number(eff[k]) || 0;
      if (tgt <= 0) return false;
      return (Number(t[k]) || 0) >= tgt * 0.9;
    });
    if (!hit) return;
    supabase
      .rpc("award_xp", {
        p_user_id:   userId,
        p_source:    "nutrition_goal_day",
        p_ref_table: null,
        p_ref_id:    null,
      })
      .then(({ error }) => {
        if (error) console.error("[award_xp] nutrition_goal_day failed:", error);
      });
  }, [userId, tally.today, eff.kcal, eff.protein_g, eff.fat_g, eff.carb_g]);

  // Active period numbers + label.
  const current = useMemo(() => {
    if (period === "day")   return tally.today        || {};
    if (period === "week")  return tally.weekTotals   || {};
    if (period === "month") return tally.monthTotals  || {};
    return {};
  }, [period, tally.today, tally.weekTotals, tally.monthTotals]);

  const periodDef = PERIODS.find(p => p.id === period);
  const series = period === "month" ? tally.dailySeriesMonth : tally.dailySeriesWeek;
  const showSparkline = period !== "day" && Array.isArray(series) && series.length > 0;

  const coverageText = (() => {
    const cov = tally.coverage;
    if (!cov || cov.total === 0) return null;
    if (cov.withNutrition === cov.total) return null;
    const untracked = cov.total - cov.withNutrition;
    return `BASED ON ${cov.withNutrition} OF ${cov.total} MEALS · ${untracked} UNTRACKED`;
  })();

  return (
    <div style={{ marginTop: 18 }}>
      <style>{`
        @keyframes ntGrowBar { from { width: 0% } to { width: var(--nt-target-w) } }
        @keyframes ntGrowHeight { from { height: 0% } to { height: var(--nt-h) } }
        @keyframes ntRise { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:translateY(0) } }
      `}</style>

      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10,
      }}>
        <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.gold, letterSpacing: "0.12em" }}>
          NUTRITION
        </div>
        <button
          onClick={() => setEditingGoals(true)}
          style={{
            background: "transparent", border: "none", cursor: "pointer",
            fontFamily: FONT.mono, fontSize: 9,
            color: COLOR.dim, letterSpacing: "0.08em",
            textDecoration: "underline dotted", textUnderlineOffset: 2,
            padding: 0,
          }}
        >
          EDIT GOALS
        </button>
      </div>

      {/* Period tabs. Single-select chip row. Keying the card body on
          the active period forces React to remount so the growBar
          animation re-runs on every tab switch. */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {PERIODS.map(p => {
          const active = p.id === period;
          return (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              style={{
                flex: 1, padding: "8px 0",
                background: active ? COLOR.gold : COLOR.ground,
                border: `1px solid ${active ? COLOR.gold : COLOR.border}`,
                color: active ? COLOR.night : COLOR.dim,
                borderRadius: 8,
                fontFamily: FONT.mono, fontSize: 10,
                fontWeight: active ? 700 : 400,
                cursor: "pointer", letterSpacing: "0.08em",
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      <div key={period} style={{ animation: "ntRise 0.3s ease backwards" }}>
        {tally.loading ? (
          <p style={{ fontFamily: FONT.sans, fontSize: 13, color: COLOR.muted, fontStyle: "italic", margin: 0 }}>
            Loading…
          </p>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {MACRO_DEFS.map((m, i) => {
                const actual = Math.round(current[m.key] || 0);
                const target = Math.round((Number(eff[m.key]) || 0) * periodDef.multiplier);
                const pct = target > 0 ? Math.min(100, (actual / target) * 100) : 0;
                return (
                  <div
                    key={m.key}
                    onClick={() => setEditingGoals(true)}
                    style={{
                      padding: "10px 12px",
                      background: COLOR.ground, border: `1px solid ${COLOR.edge}`,
                      borderRadius: 10, cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                      <span style={{
                        fontFamily: FONT.mono, fontSize: 10,
                        color: m.color, letterSpacing: "0.08em",
                      }}>
                        {m.label}
                      </span>
                      <span style={{
                        fontFamily: FONT.mono, fontSize: 11, color: COLOR.ink,
                      }}>
                        {actual.toLocaleString()}
                        <span style={{ color: COLOR.sub }}> / {target.toLocaleString()} {m.unit}</span>
                      </span>
                    </div>
                    <div style={{
                      height: 6, background: COLOR.deep, borderRadius: 4,
                      overflow: "hidden", position: "relative",
                    }}>
                      <div style={{
                        height: "100%", background: m.color,
                        borderRadius: 4,
                        "--nt-target-w": `${pct}%`,
                        width: `${pct}%`,
                        animation: `ntGrowBar 0.7s ${i * 0.12}s ease backwards`,
                        boxShadow: pct > 0 ? `0 0 8px ${m.color}40` : "none",
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {showSparkline && (
              <div style={{
                marginTop: 14, padding: "12px 10px",
                background: COLOR.deep, border: `1px solid ${COLOR.edge}`,
                borderRadius: 10,
              }}>
                <div style={{
                  fontFamily: FONT.mono, fontSize: 9,
                  color: COLOR.muted, letterSpacing: "0.08em", marginBottom: 8,
                }}>
                  {period === "week" ? "LAST 7 DAYS · KCAL" : "LAST 30 DAYS · KCAL"}
                </div>
                {(() => {
                  const max = series.reduce((n, d) => Math.max(n, d.kcal || 0), 0) || 1;
                  const minBarPct = 3;
                  return (
                    <div style={{
                      display: "flex", alignItems: "flex-end", gap: 3,
                      height: 60,
                    }}>
                      {series.map((d, i) => {
                        const pct = d.kcal > 0
                          ? Math.max(minBarPct, (d.kcal / max) * 100)
                          : minBarPct;
                        const isEmpty = !(d.meals > 0);
                        return (
                          <div
                            key={d.date}
                            title={`${d.date} · ${Math.round(d.kcal || 0)} kcal · ${d.meals} meal${d.meals === 1 ? "" : "s"}`}
                            style={{
                              flex: 1,
                              height: `${pct}%`,
                              background: isEmpty ? COLOR.soil : CHIP_TONES.canonical.fg,
                              borderRadius: 2,
                              boxShadow: isEmpty ? "none" : "0 0 6px #d4a87a30",
                              animation: `ntGrowHeight 0.5s ${i * 0.02}s ease backwards`,
                              "--nt-h": `${pct}%`,
                            }}
                          />
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {coverageText && (
              <div style={{
                marginTop: 10,
                fontFamily: FONT.mono, fontSize: 9,
                color: COLOR.sub, letterSpacing: "0.08em",
              }}>
                {coverageText}
              </div>
            )}

            {/* Itemized breakdown — every cook + every "I ate this"
                event landed on the selected period, chronological
                newest-first. Each row: emoji + title + (meal slot,
                time, amount-unit) + kcal contribution. Cook events
                show the recipe title + emoji; ingredient-eat events
                surface the canonical's name + emoji; leftover-eat
                events show "<recipe> (leftover)" with the recipe's
                emoji. Hidden when no events in the window — the
                empty-state message covers that case. */}
            {(() => {
              const events = period === "day"
                ? (tally.todayEvents || [])
                : (period === "week" ? (tally.weekEvents || []) : []);
              if (events.length === 0) return null;
              return (
                <div style={{ marginTop: 14 }}>
                  <div style={{
                    fontFamily: FONT.mono, fontSize: 10,
                    color: COLOR.dim, letterSpacing: "0.12em", marginBottom: 8,
                  }}>
                    {period === "day" ? "TODAY" : "LAST 7 DAYS"} · {events.length} EVENT{events.length === 1 ? "" : "S"}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {events.map(e => {
                      const ts = new Date(e.ts);
                      const h = ts.getHours(), mm = ts.getMinutes();
                      const ampm = h >= 12 ? "PM" : "AM";
                      const h12 = ((h + 11) % 12) + 1;
                      const timeStr = `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
                      const dayStr = period === "week"
                        ? ts.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase()
                        : null;
                      const bits = [
                        dayStr,
                        e.mealSlot ? e.mealSlot.toUpperCase() : null,
                        timeStr,
                        (e.kind === "snack" && e.amount && e.unit) ? `${e.amount} ${e.unit}` : null,
                      ].filter(Boolean);
                      return (
                        <div
                          key={e.id}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "10px 12px",
                            background: COLOR.deep, border: `1px solid ${COLOR.edge}`,
                            borderRadius: 10,
                          }}
                        >
                          <div style={{ fontSize: 22, flexShrink: 0 }}>{e.emoji}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontFamily: FONT.sans, fontSize: 13,
                              color: COLOR.ink,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                              {e.title}
                            </div>
                            <div style={{
                              fontFamily: FONT.mono, fontSize: 9,
                              color: COLOR.muted, marginTop: 2, letterSpacing: "0.06em",
                            }}>
                              {bits.join(" · ")}
                            </div>
                          </div>
                          <div style={{
                            fontFamily: FONT.mono, fontSize: 12,
                            color: e.kcal > 0 ? CHIP_TONES.canonical.fg : COLOR.muted,
                            flexShrink: 0, letterSpacing: "0.04em",
                          }}>
                            {e.kcal > 0 ? `${Math.round(e.kcal)} kcal` : "—"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {tally.today?.meals === 0 && period === "day" && (
              <p style={{
                marginTop: 10,
                fontFamily: FONT.sans, fontSize: 12,
                color: COLOR.muted, fontStyle: "italic",
              }}>
                Nothing logged today yet — cook a meal or tap "I ATE THIS" on a pantry item.
              </p>
            )}
          </>
        )}
      </div>

      {editingGoals && (
        <GoalEditor
          targets={eff}
          onClose={() => setEditingGoals(false)}
          onSave={async (patch) => {
            await onUpdateTargets?.(patch);
            setEditingGoals(false);
          }}
        />
      )}
    </div>
  );
}

function GoalEditor({ targets, onClose, onSave }) {
  const [draft, setDraft] = useState({
    kcal:      String(targets.kcal      ?? ""),
    protein_g: String(targets.protein_g ?? ""),
    fat_g:     String(targets.fat_g     ?? ""),
    carb_g:    String(targets.carb_g    ?? ""),
  });

  const rows = [
    { key: "kcal",      label: "CALORIES", unit: "kcal", color: CHIP_TONES.canonical.fg },
    { key: "protein_g", label: "PROTEIN",  unit: "g",    color: COLOR.leafMuted },
    { key: "fat_g",     label: "FAT",      unit: "g",    color: "#d4743e" },
    { key: "carb_g",    label: "CARBS",    unit: "g",    color: "#8ac8e8" },
  ];

  const save = () => {
    const patch = {};
    for (const r of rows) {
      const v = Number(draft[r.key]);
      if (Number.isFinite(v) && v > 0) patch[r.key] = v;
    }
    onSave?.(patch);
  };

  return (
    <ModalSheet onClose={onClose}>
      <div style={{ padding: "16px 20px 24px" }}>
        <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.gold, letterSpacing: "0.12em", marginBottom: 6 }}>
          DAILY GOALS
        </div>
        <h2 style={{
          fontFamily: FONT.serif, fontSize: 22, fontStyle: "italic",
          color: COLOR.ink, fontWeight: 500, margin: 0, marginBottom: 14,
        }}>
          What are you aiming for?
        </h2>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map(r => (
            <label key={r.key} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px",
              background: COLOR.ground, border: `1px solid ${COLOR.edge}`,
              borderRadius: 10,
            }}>
              <span style={{
                flex: 1,
                fontFamily: FONT.mono, fontSize: 11,
                color: r.color, letterSpacing: "0.08em",
              }}>
                {r.label}
              </span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                value={draft[r.key]}
                onChange={e => setDraft(prev => ({ ...prev, [r.key]: e.target.value }))}
                style={{
                  width: 80, padding: "6px 8px",
                  background: COLOR.deep, border: `1px solid ${COLOR.border}`,
                  color: COLOR.ink, borderRadius: 8,
                  fontFamily: FONT.mono, fontSize: 14,
                  textAlign: "right", outline: "none",
                }}
              />
              <span style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.muted, minWidth: 32 }}>
                {r.unit}
              </span>
            </label>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: "12px",
              background: COLOR.soil, border: `1px solid ${COLOR.border}`,
              color: COLOR.dim, borderRadius: 10,
              fontFamily: FONT.mono, fontSize: 11,
              letterSpacing: "0.08em", cursor: "pointer",
            }}
          >
            CANCEL
          </button>
          <button
            onClick={save}
            style={{
              flex: 2, padding: "12px",
              background: COLOR.leafMuted, border: "none",
              color: COLOR.leafDeep, borderRadius: 10,
              fontFamily: FONT.mono, fontSize: 11, fontWeight: 700,
              letterSpacing: "0.08em", cursor: "pointer",
            }}
          >
            SAVE GOALS
          </button>
        </div>
      </div>
    </ModalSheet>
  );
}
